import { useLogto } from '@logto/react';
import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import { LoginForm } from '../components/auth/LoginForm';
import { VersionBadge } from '../components/VersionBadge';
import { useAuth } from '../contexts/AuthContext';
import {
  isDevAuthMode,
  isLogtoConfigured,
  logtoRedirectUri,
} from '../lib/logto';

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, isLoading } = useAuth();
  const { signIn, isAuthenticated: isLogtoAuthed } = useLogto();

  const from = location.state?.from?.pathname || '/dashboard';

  useEffect(() => {
    if (isAuthenticated && !isLoading) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate, from]);

  useEffect(() => {
    // Dev-auth mode uses the password form; never auto-redirect to Logto.
    if (isDevAuthMode()) return;
    if (isLoading || isAuthenticated || !isLogtoConfigured) return;
    // Defer auto-signIn until Logto SDK has had a chance to rehydrate its
    // session from disk. Without this delay, a page reload that lands on
    // /login races the SDK's loadState() and we trigger signIn() even though
    // the session is valid — redirecting the user back to Logto for a
    // password they already entered. 300ms is empirically enough for SDK
    // rehydration on Chrome/Firefox.
    const t = setTimeout(() => {
      if (isLogtoAuthed) return;
      signIn(logtoRedirectUri).catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [isLoading, isAuthenticated, isLogtoAuthed, signIn]);

  const retryLogtoSignIn = () => {
    signIn(logtoRedirectUri);
  };

  if (isLoading) {
    return (
      <div className='min-h-screen flex items-center justify-center bg-gray-50'>
        <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-primary'></div>
      </div>
    );
  }

  if (isAuthenticated) {
    return null;
  }

  // Dev/mock/E2E: direct username+password login (admin/admin against the
  // mock or locally seeded auth endpoint). Compile-time false in production
  // builds — see isDevAuthMode in src/lib/logto.ts.
  if (isDevAuthMode()) {
    return (
      <div className='min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6'>
        <div className='max-w-md w-full space-y-8'>
          <div className='text-center'>
            <h1 className='text-3xl font-bold text-gray-900 mb-2'>
              AI Service Admin Portal
            </h1>
            <p className='text-gray-600'>Development sign-in</p>
          </div>

          <LoginForm />

          <div className='text-center space-y-4'>
            <p className='text-xs text-gray-500'>
              © 2024 AI Service Admin Portal. All rights reserved.
            </p>
            <div className='flex justify-center'>
              <VersionBadge />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:grid-cols-6 lg:gap-8'>
      <div className='max-w-md w-full space-y-8'>
        <div className='text-center'>
          <h1 className='text-3xl font-bold text-gray-900 mb-2'>
            AI Service Admin Portal
          </h1>
          <p className='text-gray-600'>Secure access via Logto SSO</p>
        </div>

        {isLogtoConfigured ? (
          <button
            type='button'
            data-testid='sso-signin-button'
            onClick={retryLogtoSignIn}
            className='w-full flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
          >
            Sign in with SSO (Logto)
          </button>
        ) : (
          <p className='text-sm text-red-600 text-center'>
            Logto SSO is not configured (VITE_LOGTO_APP_ID is unset). Contact
            the platform team.
          </p>
        )}

        <div className='text-center space-y-4'>
          <p className='text-xs text-gray-500'>
            © 2024 AI Service Admin Portal. All rights reserved.
          </p>
          <div className='flex justify-center'>
            <VersionBadge />
          </div>
        </div>
      </div>
    </div>
  );
}

export default LoginPage;
